/**
 * IndexingPipelineView (TASK-261 — Indexing Pipeline Visualizer)
 *
 * Renders the latest indexing run as a visual pipeline schema (a stepper).
 * Each stage is a card showing its kind (🤖 LLM bot / ⚙️ deterministic code
 * step) and outcome status (done / failed / skipped). Clicking a stage that
 * carries a heavy artifact (`has_artifact === true`) asks the parent to load
 * its detail via `onStageClick(stage.order)`.
 *
 * States (TASK-266 — live progress):
 *   - run with stages, run.status==='running' OR no source processing
 *                             → the pipeline schema with per-stage live status
 *                               (running stage shows a spinner, pending dimmed,
 *                               failed stage shows its error inline).
 *   - isRunning but no live run schema to show (run null / stale)
 *                             → rotating waiting message (WAITING_MESSAGES)
 *                               + a "task may be lost" hint.
 *   - run === null, idle      → "Запустите индексацию" placeholder.
 */
import { useState, useEffect } from 'react';
import {
  Bot,
  Cog,
  Check,
  AlertCircle,
  MinusCircle,
  Circle,
  Loader2,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  IndexingRun,
  IndexingStageSummary,
  StageStatus,
} from '@/types/indexingRun';

export interface IndexingPipelineViewProps {
  run: IndexingRun | null;
  onStageClick: (order: number) => void;
  isRunning?: boolean;
}

// ── Waiting state (isRunning but no live run yet) ───────────────────────────
const WAITING_MESSAGES = [
  'Задача поставлена в очередь…',
  'Скачиваем и нормализуем документы…',
  'Готовим данные для AI-анализа…',
  'Запускаем Bot 1 · Границы услуг…',
  'Ещё немного — совсем скоро начнётся…',
];

function RotatingMessage(): JSX.Element {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % WAITING_MESSAGES.length);
        setVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <span
      className={cn(
        'text-sm text-blue-600 transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0'
      )}>
      {WAITING_MESSAGES[idx]}
    </span>
  );
}

function PipelineWaitingState(): JSX.Element {
  return (
    <div
      data-testid="indexing-running"
      className="flex flex-col items-center justify-center gap-4 rounded-xl border border-blue-200 bg-blue-50 py-10 px-6 text-center">

      {/* Animated robot studying scene */}
      <div className="flex items-center gap-3">
        {/* Robot with pulsing ring */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-25" />
          <div className="relative rounded-full bg-blue-100 p-3 border border-blue-300">
            <Bot className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        {/* Animated dots */}
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce [animation-delay:0ms]" />
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce [animation-delay:150ms]" />
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce [animation-delay:300ms]" />
        </div>
        {/* Document icon */}
        <div className="rounded-full bg-indigo-100 p-3 border border-indigo-200 animate-pulse">
          <FileText className="h-6 w-6 text-indigo-500" />
        </div>
      </div>

      {/* Shimmer progress bar */}
      <div className="relative w-full max-w-xs h-1.5 rounded-full bg-blue-100 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-100 via-blue-400 to-blue-100 animate-shimmer bg-[length:200%_100%]" />
      </div>

      {/* Rotating status message */}
      <RotatingMessage />

      {/* Fallback hint */}
      <p className="max-w-xs text-xs text-blue-400">
        Если индексация долго не начинается — задача могла потеряться,
        попробуйте перезапустить.
      </p>
    </div>
  );
}

// ── Status visuals ───────────────────────────────────────────────────────────
const STATUS_STYLES: Record<StageStatus, string> = {
  // 'running' is the current step — make it pop with a blue accent + ring.
  running: 'border-blue-400 bg-blue-50 ring-1 ring-blue-300',
  // 'pending' has not been reached yet — dim it so the eye lands on running.
  pending: 'border-gray-200 bg-gray-50 opacity-50',
  done: 'border-green-300 bg-green-50',
  failed: 'border-red-300 bg-red-50',
  skipped: 'border-gray-200 bg-gray-50 opacity-70',
};

function StatusIcon({ status }: { status: StageStatus }): JSX.Element {
  switch (status) {
    case 'running':
      return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
    case 'pending':
      return <Circle className="h-4 w-4 text-gray-300" />;
    case 'done':
      return <Check className="h-4 w-4 text-green-600" />;
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    case 'skipped':
    default:
      return <MinusCircle className="h-4 w-4 text-gray-400" />;
  }
}

// ── Single stage card ────────────────────────────────────────────────────────
function StageCard({
  stage,
  onStageClick,
}: {
  stage: IndexingStageSummary;
  onStageClick: (order: number) => void;
}): JSX.Element {
  const KindIcon = stage.kind === 'llm' ? Bot : Cog;
  // A running stage is clickable so the user can inspect its live artifact
  // (input_summary / prompt are populated before output/response land). Other
  // statuses (done/failed/skipped) stay gated on has_artifact as before.
  const clickable = stage.status === 'running' || stage.has_artifact;

  // Render the metrics compactly (e.g. "scopes: 12 · sources: 2").
  const metricEntries = Object.entries(stage.metrics ?? {});

  return (
    <button
      type="button"
      data-testid={`indexing-stage-${stage.order}`}
      data-kind={stage.kind}
      data-status={stage.status}
      disabled={!clickable}
      aria-disabled={!clickable}
      onClick={() => {
        if (clickable) onStageClick(stage.order);
      }}
      title={clickable ? 'Показать артефакт этапа' : 'Артефакт недоступен'}
      className={cn(
        'flex w-full flex-col gap-1.5 rounded-lg border p-3 text-left transition-all',
        STATUS_STYLES[stage.status],
        clickable ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'
      )}>
      <div className="flex items-center gap-2">
        <KindIcon
          className={cn(
            'h-4 w-4 shrink-0',
            stage.kind === 'llm' ? 'text-purple-600' : 'text-slate-500'
          )}
        />
        <StatusIcon status={stage.status} />
        <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-gray-400">
          {stage.kind === 'llm' ? 'LLM' : 'CODE'}
        </span>
      </div>

      <p className="text-sm font-medium text-gray-900">{stage.title}</p>

      {metricEntries.length > 0 && (
        <p className="text-xs text-gray-500">
          {metricEntries.map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
        </p>
      )}

      {/* Inline failure reason for a failed stage (short; full text in Sheet). */}
      {stage.status === 'failed' && stage.error && (
        <p
          data-testid={`indexing-stage-${stage.order}-error`}
          className="line-clamp-2 text-xs font-medium text-red-600"
          title={stage.error}>
          {stage.error}
        </p>
      )}

      {stage.duration_ms != null && (
        <p className="text-[11px] text-gray-400">{stage.duration_ms} мс</p>
      )}
    </button>
  );
}

// ── Pipeline schema ──────────────────────────────────────────────────────────
export function IndexingPipelineView({
  run,
  onStageClick,
  isRunning = false,
}: IndexingPipelineViewProps): JSX.Element {
  // A live run is the source of truth for "show the schema now": its stages
  // carry per-step statuses (running/pending/done/failed) so the user sees
  // exactly which step is executing.
  const runIsLive = run?.status === 'running';

  // Show the schema when we have stages AND either the run is live (mid-flight)
  // or no source is processing (post-hoc view of the last completed/failed run).
  // Withhold it only when a source is processing but the run we hold is stale
  // (not live) — that gap is the "waiting / possibly lost" case below.
  if (run !== null && run.stages.length > 0 && (runIsLive || !isRunning)) {
    return (
      <div data-testid="indexing-pipeline" className="space-y-4">
        {/* TASK-267: the red "Причины сбоя" panel is a FAILURE signal — show it
            ONLY for a failed run. A successful run that happens to carry info
            notes in `reasons` must never look broken. */}
        {run.status === 'failed' && run.reasons.length > 0 && (
          <div
            data-testid="indexing-failure-reasons"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <p className="mb-1 font-medium">Причины сбоя:</p>
            <ul className="list-inside list-disc space-y-0.5">
              {run.reasons.map((reason, idx) => (
                <li key={idx}>{reason}</li>
              ))}
            </ul>
          </div>
        )}

        {runIsLive && (() => {
          const total = run.stages.length;
          const done = run.stages.filter(
            (s) => s.status === 'done' || s.status === 'skipped' || s.status === 'failed'
          ).length;
          const running = run.stages.find((s) => s.status === 'running');
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <div className="mb-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="font-medium text-blue-700">
                  {running ? `⚙ ${running.title}` : 'Завершение…'}
                </span>
                <span>{done} из {total} этапов · {pct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-700 ease-out"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {run.stages.map((stage) => (
            <StageCard
              key={stage.order}
              stage={stage}
              onStageClick={onStageClick}
            />
          ))}
        </div>
      </div>
    );
  }

  // A source is processing but we have no live schema to show — either there is
  // no run yet, or the run we hold is stale (a previous success/failure that
  // hasn't been overwritten by the new task yet). Show a clear "waiting" state
  // instead of an eternal blind spinner, and hint that the task may be lost.
  if (isRunning) {
    return <PipelineWaitingState />;
  }

  // No run and nothing processing → invite to start indexing.
  return (
    <div
      data-testid="indexing-empty"
      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-12 text-gray-400">
      <Cog className="h-10 w-10" />
      <p className="text-sm font-medium">Запустите индексацию</p>
    </div>
  );
}

export default IndexingPipelineView;
